import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useTouchPanelStore } from './touchPanel';
import type { Device } from '@/types';

export type UserDrawerTab = 'user' | 'chats' | 'agent' | 'preferences';

export const useUIStore = defineStore('ui', () => {
  // ── Left sidebar ──────────────────────────────────────────────────────────
  const sidebarExpanded = ref(true);
  const mobileSidebarOpen = ref(false);

  // ── Sidebar (main menu) edit mode ────────────────────────────────────────
  const sidebarEditMode = ref(false);

  // ── User drawer ───────────────────────────────────────────────────────────
  const userDrawerOpen = ref(false);
  const userDrawerTab = ref<UserDrawerTab>('user');

  // ── Status drawer ─────────────────────────────────────────────────────────
  const statusDrawerOpen = ref(false);
  const statusDrawerPinned = ref(
    typeof localStorage !== 'undefined' && localStorage.getItem('statusDrawerPinned') === 'true',
  );
  const userDrawerPinned = ref(
    typeof localStorage !== 'undefined' && localStorage.getItem('userDrawerPinned') === 'true',
  );

  // ── Playlist drawer ───────────────────────────────────────────────────────
  const playlistDrawerOpen = ref(false);
  const playlistDrawerPinned = ref(
    typeof localStorage !== 'undefined' && localStorage.getItem('playlistDrawerPinned') === 'true',
  );

  // ── Site drawer (the house at a glance) ───────────────────────────────────
  const siteDrawerOpen = ref(false);
  const siteDrawerPinned = ref(
    typeof localStorage !== 'undefined' && localStorage.getItem('siteDrawerPinned') === 'true',
  );

  // ── Global device control modal ───────────────────────────────────────────
  const deviceControlModalDevice = ref<Device | null>(null);
  const openDeviceControlModal = (device: Device) => {
    deviceControlModalDevice.value = device;
  };
  const closeDeviceControlModal = () => {
    deviceControlModalDevice.value = null;
  };

  // ── Agent FAB preference ──────────────────────────────────────────────────
  const showAgentFab = ref(typeof localStorage === 'undefined' || localStorage.getItem('showAgentFab') !== 'false');

  // ── Demo FAB preference (card D2, plan §4.8) ──────────────────────────────
  // The user's own toggle, mirroring `showAgentFab`: whether the hub IS a demo
  // and wants a FAB is the hub's word (`GET /api/demo` → `mode`), and this only
  // adds the operator's "not on my screen". Default on — a demo hub's whole
  // point is that the player is there.
  const showDemoFab = ref(typeof localStorage === 'undefined' || localStorage.getItem('showDemoFab') !== 'false');

  // ── Chat quick-open ───────────────────────────────────────────────────────
  const chatOpenPersonId = ref<string | null>(null);

  // ── Launcher account menu ─────────────────────────────────────────────────
  // Bumped when something outside the menu (the Electron bar's account button)
  // asks it to open; the menu watches the counter.
  const accountMenuSignal = ref(0);
  const requestAccountMenu = () => {
    accountMenuSignal.value += 1;
  };

  // ── Left sidebar actions ──────────────────────────────────────────────────
  const toggleSidebar = () => {
    sidebarExpanded.value = !sidebarExpanded.value;
  };
  const openSidebar = () => {
    sidebarExpanded.value = true;
  };
  const closeSidebar = () => {
    sidebarExpanded.value = false;
  };
  const toggleMobileSidebar = () => {
    mobileSidebarOpen.value = !mobileSidebarOpen.value;
  };
  const closeMobileSidebar = () => {
    mobileSidebarOpen.value = false;
  };

  const setSidebarEditMode = (value: boolean) => {
    sidebarEditMode.value = value;
    if (value) sidebarExpanded.value = true;
  };

  // ── User drawer actions ───────────────────────────────────────────────────
  const openUserDrawer = (tab?: UserDrawerTab) => {
    if (tab) userDrawerTab.value = tab;
    userDrawerOpen.value = true;
    statusDrawerOpen.value = false; // close status drawer if open
    playlistDrawerOpen.value = false;
    siteDrawerOpen.value = false;
  };

  const closeUserDrawer = () => {
    userDrawerOpen.value = false;
  };
  const toggleUserDrawerPin = () => {
    userDrawerPinned.value = !userDrawerPinned.value;
    localStorage.setItem('userDrawerPinned', String(userDrawerPinned.value));
  };

  const toggleShowAgentFab = () => {
    showAgentFab.value = !showAgentFab.value;
    localStorage.setItem('showAgentFab', String(showAgentFab.value));
  };

  const toggleShowDemoFab = () => {
    showDemoFab.value = !showDemoFab.value;
    localStorage.setItem('showDemoFab', String(showDemoFab.value));
  };

  const openChatWithPerson = (userId: string) => {
    chatOpenPersonId.value = userId;
    openUserDrawer('chats');
  };

  // ── Status drawer actions ─────────────────────────────────────────────────
  const openStatusDrawer = () => {
    statusDrawerOpen.value = true;
    userDrawerOpen.value = false;
    playlistDrawerOpen.value = false;
    siteDrawerOpen.value = false;
  };
  const closeStatusDrawer = () => {
    statusDrawerOpen.value = false;
  };
  const toggleStatusDrawer = () => {
    if (statusDrawerOpen.value) closeStatusDrawer();
    else openStatusDrawer();
  };
  const toggleStatusDrawerPin = () => {
    statusDrawerPinned.value = !statusDrawerPinned.value;
    localStorage.setItem('statusDrawerPinned', String(statusDrawerPinned.value));
  };

  // ── Playlist drawer actions ───────────────────────────────────────────────
  const openPlaylistDrawer = () => {
    playlistDrawerOpen.value = true;
    userDrawerOpen.value = false;
    statusDrawerOpen.value = false;
    siteDrawerOpen.value = false;
  };
  const closePlaylistDrawer = () => {
    playlistDrawerOpen.value = false;
  };
  const togglePlaylistDrawer = () => {
    if (playlistDrawerOpen.value) closePlaylistDrawer();
    else openPlaylistDrawer();
  };
  const togglePlaylistDrawerPin = () => {
    playlistDrawerPinned.value = !playlistDrawerPinned.value;
    localStorage.setItem('playlistDrawerPinned', String(playlistDrawerPinned.value));
  };

  // ── Site drawer actions ───────────────────────────────────────────────────
  const openSiteDrawer = () => {
    siteDrawerOpen.value = true;
    userDrawerOpen.value = false;
    statusDrawerOpen.value = false;
    playlistDrawerOpen.value = false;
  };
  const closeSiteDrawer = () => {
    siteDrawerOpen.value = false;
  };
  const toggleSiteDrawer = () => {
    if (siteDrawerOpen.value) closeSiteDrawer();
    else openSiteDrawer();
  };
  const toggleSiteDrawerPin = () => {
    siteDrawerPinned.value = !siteDrawerPinned.value;
    localStorage.setItem('siteDrawerPinned', String(siteDrawerPinned.value));
  };

  // ── Panel mode ────────────────────────────────────────────────────────────
  const isPanelMode = computed(() => {
    try {
      const touchPanel = useTouchPanelStore();
      return touchPanel.isPanelMode;
    } catch {
      return false;
    }
  });

  return {
    sidebarExpanded,
    mobileSidebarOpen,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    toggleMobileSidebar,
    closeMobileSidebar,
    sidebarEditMode,
    setSidebarEditMode,
    userDrawerOpen,
    userDrawerTab,
    userDrawerPinned,
    openUserDrawer,
    closeUserDrawer,
    toggleUserDrawerPin,
    showAgentFab,
    toggleShowAgentFab,
    showDemoFab,
    toggleShowDemoFab,
    chatOpenPersonId,
    openChatWithPerson,
    accountMenuSignal,
    requestAccountMenu,
    statusDrawerOpen,
    openStatusDrawer,
    closeStatusDrawer,
    toggleStatusDrawer,
    statusDrawerPinned,
    toggleStatusDrawerPin,
    playlistDrawerOpen,
    openPlaylistDrawer,
    closePlaylistDrawer,
    togglePlaylistDrawer,
    playlistDrawerPinned,
    togglePlaylistDrawerPin,
    siteDrawerOpen,
    openSiteDrawer,
    closeSiteDrawer,
    toggleSiteDrawer,
    siteDrawerPinned,
    toggleSiteDrawerPin,
    isPanelMode,
    deviceControlModalDevice,
    openDeviceControlModal,
    closeDeviceControlModal,
  };
});
